import {AsyncPipe, NgClass} from '@angular/common';
import {AfterViewInit, Component, DOCUMENT, Inject, OnDestroy} from '@angular/core';
import {Meta} from '@angular/platform-browser';
import {ActivatedRoute, NavigationExtras} from '@angular/router';
import {OCC_USER_ID_ANONYMOUS, RoutingService, WindowRef} from '@spartacus/core';
import {UserAccountFacade} from '@spartacus/user/account/root';
import {Observable, Subscription} from 'rxjs';
import {map} from 'rxjs/operators';
import {AimoSpinnerModule} from './features/cms-components/shared/spinner/aimo-spinner.module';
import {AimoRoutingService} from './features/cms-structure/routing/aimo-routing.service';
import {AimoHamburgerMenuService} from './features/layout/hamburger-menu/aimo-hamburger-menu.service';
import {AimoMainModule} from './features/layout/main/aimo-main.module';
import {AimoUser} from './features/model/user.model';
import {AimoGtmService} from './features/service/gtm/aimo-gtm.service';
import {AimoWebextendService} from './features/service/webextend/aimo-webextend.service';
import {AimoActiveCartService} from "./features/service/cart/aimo-active-cart.service";

declare const __ucCmp: any;

@Component({
    selector: 'aimo-root',
    templateUrl: 'app.component.html',
    styleUrls: ['./app.component.scss'],
    imports: [AsyncPipe, AimoMainModule, NgClass, AimoSpinnerModule],
})
export class AppComponent implements AfterViewInit, OnDestroy {
    title: string = 'Aimo';
    isExpanded$: Observable<boolean>;
    subscriptions: Subscription = new Subscription();

    constructor(
        private hamburgerMenuService: AimoHamburgerMenuService,
        protected aimoRoutingService: AimoRoutingService,
        protected gtmService: AimoGtmService,
        protected aimoWebextendService: AimoWebextendService,
        protected activatedRoute: ActivatedRoute,
        protected userAccountFacade: UserAccountFacade,
        private metaService: Meta,
        protected routingService: RoutingService,
        @Inject(DOCUMENT) protected document: Document,
        protected winRef: WindowRef,
        protected aimoActiveCartService: AimoActiveCartService,
    ) {
        this.isExpanded$ = this.hamburgerMenuService.isExpanded;
    }

    isPikatukku(): boolean {
        return this.aimoRoutingService.isPikatukku();
    }

    ngAfterViewInit() {
        const gtmUrlParam = this.activatedRoute.snapshot.queryParamMap.get('_gl');
        this.gtmService.addToDom(gtmUrlParam);
        this.aimoWebextendService.initWebextend();
        this.addQualtricsMeta();
        this.handleMobileAppEvents();
        // this.appendStyle('bootstrap.css', false);
        // this.appendStyle('styles.css', true);
    }

    private handleMobileAppEvents(): void {
        this.document.addEventListener('message', (message: MessageEvent) => {
            const reload = message.data?.reload === true || message.data?.reload === 'true';
            const opts = {
                onSameUrlNavigation: 'reload',
                queryParams: {'reload': reload}
            } as NavigationExtras;
            if (message.data?.type === 'favorites') {
                this.routingService.go('/category/favorites', opts);
            } else if (message.data?.type === 'cart') {
                if (reload) {
                    this.aimoActiveCartService.reloadCart();
                }
                this.routingService.go('/cart', opts);
            } else if (message.data?.type === 'ordertemplates') {
                this.routingService.go('/ordertemplates', opts);
            } else if (message.data?.type === 'product') {
                this.routingService.go('/p/' + message.data.code, opts);
            }
        });
    }

    private appendStyle(name: string, asynchronous: boolean): void {
        const style: any = this.document.createElement('link');
        style.rel = 'stylesheet';
        style.href = name;
        style.async = asynchronous;
        style.type = 'text/css';
        style.media = 'all';
        style.id = name;
        this.document.head.appendChild(style);
    }

    private addQualtricsMeta(): void {
        this.subscriptions.add(
            this.userAccountFacade
                .get()
                .pipe(map((user1) => user1 as AimoUser))
                .subscribe((user1) => {
                    const uid = user1?.uid;
                    if (uid != undefined && uid !== OCC_USER_ID_ANONYMOUS) {
                        this.addOrUpdateTag('userId', user1.uid);
                        this.addOrUpdateTag('email', user1.email);
                        this.addOrUpdateTag('CustomerCompany', user1.unit?.name);
                        this.addOrUpdateTag('PhoneNumber', user1.unit?.addresses[0]?.phone);
                        this.addOrUpdateTag('CustomerID', user1.unit?.uid);
                    } else {
                        this.metaService.removeTag("name='userId'");
                        this.metaService.removeTag("name='email'");
                        this.metaService.removeTag("name='CustomerCompany'");
                        this.metaService.removeTag("name='PhoneNumber'");
                        this.metaService.removeTag("name='CustomerID'");
                    }
                }),
        );
    }

    private addOrUpdateTag(metaSelector: string, newContent: string): void {
        const qualtricsId = 'qualtrics_' + metaSelector;
        if (!this.metaService.getTag('name=' + metaSelector)) {
            this.metaService.updateTag(
                {
                    name: metaSelector,
                    id: qualtricsId,
                    content: newContent,
                },
                'name=' + metaSelector,
            );
        } else {
            this.metaService.addTag({name: metaSelector, id: qualtricsId, content: newContent});
        }
    }

    ngOnDestroy(): void {
        this.subscriptions.unsubscribe();
    }
}
